<script setup lang="ts">
import { computed, ref, toRef, unref, watch } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import StandardInput from '@/components/StandardInput.vue';
import ImageField from '@/components/ImageField.vue';

import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useFormsStore } from '@/stores';
import { localizableString } from '@/helpers/validation.helper';
import { formatMoney, provideEditLocale } from '@/composables/data-display-utils';
import type { DotDto, Locales } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useLanguageValidationErrors } from '@/composables';
import { Constants } from '@/constants';

// CONST
const { t } = useI18n();

// STORE
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  dot: DotDto;
  stepId: number;
  formId: number;
  languageEnabled: Locales[];
  dotVotingType: number;
  dotVotingMeasurementType: number;
  defaultCostPerChoice: number;
  numberOfSteps?: number;
}>();

// REFS
const costHint = ref('');

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const imageRef = ref(props.dot.image || { url: null });
const imageDataRef = ref<string | null>(null);

// FORM
const { handleSubmit, errors, meta, setFieldValue, values } = useForm({
  initialValues: props.dot,
  validationSchema: yup.object<DotDto>({
    title: localizableString(props.languageEnabled),
    short_text: localizableString(props.languageEnabled),
    long_text: localizableString(props.languageEnabled, { optional: true }),
    cost: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' || originalValue === null || originalValue === undefined ? undefined : value;
      })
      .typeError(() =>
        t('validation.invalid_number', {
          field: t('validation.forms.dot-voting.cost.field-name'),
        }),
      )
      .optional(),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// COMPUTED
const dialogTitle = computed(() =>
  props.mode === 'add' ? t('form.dialogs.dot-voting.create') : t('form.dialogs.dot-voting.edit'),
);

const isDirty = computed(() => {
  return unref(meta).dirty;
});

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  await wrapSavingOperation(
    async () =>
      await formsStore.saveDotVotingChoice(values, props.formId, props.stepId, {
        image: imageDataRef.value,
        version: 'v1',
      }),
    () => onDialogOK(),
  );
});

const handleClearImage = async () => {
  imageRef.value = { url: null };
  imageDataRef.value = null;
  setFieldValue('image', null);
};

const handleSaveImage = async (options: SavingOptions) => {
  imageRef.value.url = options.image!;
  imageDataRef.value = options.image!;
};

// WATCH
watch(
  () => values.cost,
  (newCost) => {
    if (props.dotVotingType === Constants.Form.DotVoting.Type.Variable && newCost && props.numberOfSteps) {
      costHint.value =
        props.dotVotingMeasurementType === Constants.Form.DotVoting.MeasurementType.Money
          ? t('validation.forms.dot-voting.cost.dollar-cost-per-step', {
              cost: formatMoney(newCost / props.numberOfSteps),
            })
          : t('validation.forms.dot-voting.cost.points-cost-per-step', { cost: newCost / props.numberOfSteps });
    } else {
      costHint.value = t('validation.forms.dot-voting.cost.override');
    }
  },
  { immediate: true },
);
</script>

<template>
  <q-dialog class="dot-vote-dialog" ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.dot" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="title"
          :label="t('form.dot-voting.fields.title')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <localized-input
          name="short_text"
          :label="t('form.dot-voting.fields.short-text')"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
          :maxLength="100"
          :auto-grow="true"
        />
        <q-field :label="t('form.dot-voting.fields.long-text')" stack-label borderless>
          <template v-slot:control>
            <localized-editor
              name="long_text"
              :label="t('form.dot-voting.fields.long-text')"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="props.languageEnabled"
              disable-sanitize-html
            />
          </template>
        </q-field>
        <standard-input name="cost" :label="t('form.dot-voting.fields.cost')" :hint="costHint" type="number" outlined />
        <image-field
          :label="t('form.dot-voting.fields.image')"
          :source="imageRef.url"
          :target-width="500"
          :target-height="500"
          @clear-image="handleClearImage"
          @save-image="handleSaveImage"
          :form-dirty="isDirty"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="$t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="$t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style lang="scss">
.dot-vote-dialog {
  .localized-editor {
    margin-bottom: $space-lg;
    margin-top: 0;
  }
}
</style>
